import { Account } from "models/Account";
import React, { useEffect, useRef, useState } from "react";
import {
    AccountAsset,
    AccountAssetNote,
    getAccountAssetNotes,
    getAccountAssets,
} from "services/AccountApi";
import { useAccountTranslation } from "./translations";

interface Props {
    customer: Account;
}

interface AssetLogbook {
    asset: AccountAsset;
    notes: AccountAssetNote[];
}

const typeIcon = (type: string): string => ({
    server: "mdi-server",
    switch: "mdi-switch",
    router: "mdi-router-network",
    ups: "mdi-battery-charging",
    generator: "mdi-engine",
    cooling: "mdi-air-conditioner",
    pdu: "mdi-power-plug",
    storage: "mdi-database",
    disk: "mdi-harddisk",
    internet_circuit: "mdi-access-point-network",
    customer_network: "mdi-lan",
    managed_vm: "mdi-cloud-outline",
    computer: "mdi-desktop-tower-monitor",
    rack: "mdi-server-network",
    location: "mdi-map-marker-outline",
    datacenter: "mdi-office-building-marker-outline",
}[type] ?? "mdi-cube-outline");

const readableValue = (value: string) => value.replaceAll("_", " ");

const AccountAssets: React.FC<Props> = ({ customer }) => {
    const { t, intlLocale } = useAccountTranslation();
    const [logbooks, setLogbooks] = useState<AssetLogbook[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const loadSequence = useRef(0);

    useEffect(() => {
        const sequence = ++loadSequence.current;
        setLoading(true);
        setError(false);
        setLogbooks([]);

        getAccountAssets(customer.id)
            .then(assets => Promise.all(assets.map(async asset => ({
                asset,
                notes: await getAccountAssetNotes(customer.id, asset.id),
            }))))
            .then(values => {
                if (sequence === loadSequence.current) setLogbooks(values);
            })
            .catch(() => {
                if (sequence === loadSequence.current) setError(true);
            })
            .finally(() => {
                if (sequence === loadSequence.current) setLoading(false);
            });
    }, [customer.id]);

    const formatDate = (value: string) => new Intl.DateTimeFormat(intlLocale, {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(new Date(value));

    return <section className="portal-assets-page">
        <header className="portal-page-heading">
            <span className="portal-eyebrow">{customer.legal_name}</span>
            <h1>{t("assets")}</h1>
            <p>{t("assetsIntro")}</p>
        </header>

        {error && <div className="portal-assets-alert" role="alert">
            <i className="mdi mdi-alert-circle-outline" aria-hidden="true" />
            {t("assetsLoadError")}
        </div>}

        {loading && <div className="portal-loading-row">
            <i className="mdi mdi-loading mdi-spin" aria-hidden="true" />
            {t("loadingAssets")}
        </div>}

        {!loading && !error && logbooks.length === 0 && <div className="portal-panel portal-assets-empty">
            <i className="mdi mdi-cube-outline" aria-hidden="true" />
            <strong>{t("noAssets")}</strong>
        </div>}

        {!loading && !error && <div className="portal-asset-list">
            {logbooks.map(({ asset, notes }) => <article className="portal-asset-card" key={asset.id}>
                <header className="portal-asset-header">
                    <span className="portal-asset-icon" aria-hidden="true">
                        <i className={`mdi ${typeIcon(asset.type)}`} />
                    </span>
                    <div className="portal-asset-title">
                        <h2>{asset.name}</h2>
                        <span>{readableValue(asset.type)}</span>
                    </div>
                    <span className={`portal-asset-status portal-asset-status--${asset.status}`}>
                        {readableValue(asset.status)}
                    </span>
                </header>

                {(asset.location || asset.serial_number || asset.manufacturer || asset.model) &&
                    <dl className="portal-asset-meta">
                        {asset.location && <div><dt>{t("location")}</dt><dd>{asset.location}</dd></div>}
                        {asset.serial_number && <div><dt>{t("serialNumber")}</dt><dd>{asset.serial_number}</dd></div>}
                        {(asset.manufacturer || asset.model) && <div><dt>{t("assetModel")}</dt><dd>{[asset.manufacturer, asset.model].filter(Boolean).join(" ")}</dd></div>}
                    </dl>}
                {asset.description && <p className="portal-asset-description">{asset.description}</p>}

                <section className="portal-logbook" aria-labelledby={`asset-logbook-${asset.id}`}>
                    <div className="portal-logbook-heading">
                        <h3 id={`asset-logbook-${asset.id}`}>
                            <i className="mdi mdi-book-open-page-variant-outline" aria-hidden="true" />
                            {t("assetLogbook")}
                        </h3>
                        <span>{notes.length}</span>
                    </div>
                    {notes.length === 0
                        ? <p className="portal-logbook-empty">{t("noAssetNotes")}</p>
                        : <ol className="portal-logbook-list">
                            {notes.map(note => <li key={note.id}>
                                <div className="portal-note-marker" aria-hidden="true" />
                                <div className="portal-note-body">
                                    <div className="portal-note-meta">
                                        <span>{readableValue(note.type)}</span>
                                        <time dateTime={note.created_at}>{formatDate(note.created_at)}</time>
                                    </div>
                                    <h4>{note.title}</h4>
                                    {note.content && <p>{note.content}</p>}
                                    {note.result && <p className="portal-note-result">
                                        <strong>{t("result")}:</strong> {readableValue(note.result)}
                                    </p>}
                                    <footer>
                                        {t("byAuthor", { author: note.author_name || t("unknownAuthor") })}
                                        {note.edited_at ? ` · ${t("noteEdited")}` : ""}
                                    </footer>
                                </div>
                            </li>)}
                        </ol>}
                </section>
            </article>)}
        </div>}
    </section>;
};

export default AccountAssets;
